import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth, authFetch } from "../App";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { Plus, Trash2, Navigation, MapPin, Clock, RefreshCw, GripVertical } from "lucide-react";

const API = '/api';

const roleLabels = {
  admin: "Admin",
  projektleiter: "Projektleiter",
  mitarbeiter: "Mitarbeiter",
  nachunternehmer: "Nachunternehmer",
};

export default function UserManagement() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [todayStatus, setTodayStatus] = useState({});
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(null);
  const autoRefreshRef = useRef(null);
  const dragIndexRef = useRef(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);

  useEffect(() => {
    if (!user?.permissions?.can_manage_users) navigate("/app");
  }, [user, navigate]);

  useEffect(() => { loadUsers(); }, []);

  const loadUsers = async () => {
    try {
      const [usersRes, statusRes] = await Promise.all([
        authFetch(`${API}/users`),
        authFetch(`${API}/clock/today-all`),
      ]);
      setUsers(await usersRes.json());
      if (statusRes.ok) {
        setTodayStatus(await statusRes.json());
      }
      setLastRefresh(new Date());
    } catch { toast.error("Fehler beim Laden"); }
    finally { setLoading(false); }
  };

  // Silent refresh — only updates today-status without full reload
  const refreshStatus = useCallback(async () => {
    try {
      const res = await authFetch(`${API}/clock/today-all`);
      if (res.ok) {
        setTodayStatus(await res.json());
        setLastRefresh(new Date());
      }
    } catch { /* silent */ }
  }, []);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    autoRefreshRef.current = setInterval(refreshStatus, 30000);
    return () => clearInterval(autoRefreshRef.current);
  }, [refreshStatus]);

  const handleDeleteUser = async (userId) => {
    if (!confirm("Benutzer wirklich löschen?")) return;
    try {
      const res = await authFetch(`${API}/users/${userId}`, { method: "DELETE" });
      if (res.ok) { toast.success("Gelöscht"); loadUsers(); }
      else { const e = await res.json(); toast.error(e.detail || "Fehler"); }
    } catch { toast.error("Fehler"); }
  };

  // ── Drag & Drop: Mitarbeiter-Reihenfolge ──
  const handleDragStart = (e, index) => {
    dragIndexRef.current = index;
    e.dataTransfer.effectAllowed = "move";
    try { e.dataTransfer.setData("text/plain", String(index)); } catch { /* noop */ }
  };
  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (index !== dragOverIndex) setDragOverIndex(index);
  };
  const handleDragLeave = () => setDragOverIndex(null);
  const handleDrop = async (e, dropIndex) => {
    e.preventDefault();
    const fromIndex = dragIndexRef.current;
    dragIndexRef.current = null;
    setDragOverIndex(null);
    if (fromIndex === null || fromIndex === dropIndex) return;
    // Optimistic re-order
    const reordered = [...users];
    const [moved] = reordered.splice(fromIndex, 1);
    reordered.splice(dropIndex, 0, moved);
    setUsers(reordered);
    // Persistieren
    try {
      const res = await authFetch(`${API}/users/reorder`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user_ids: reordered.map(u => u.id) }),
      });
      if (!res.ok) {
        toast.error("Reihenfolge konnte nicht gespeichert werden");
        loadUsers();
      } else {
        toast.success("Reihenfolge aktualisiert");
      }
    } catch {
      toast.error("Reihenfolge konnte nicht gespeichert werden");
      loadUsers();
    }
  };

  if (loading) return <div className="py-4 text-[12px] text-gray-400">Lade…</div>;

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-3">
          <h2 className="text-[15px] font-semibold" style={{ color: "var(--tga-text-primary)" }}>Mitarbeiter</h2>
          <span className="text-[11px]" style={{ color: "var(--tga-text-muted)" }}>{users.length} Einträge</span>
          {lastRefresh && (
            <span className="flex items-center gap-1 text-[10px]" style={{ color: "var(--tga-text-muted)" }} data-testid="auto-refresh-indicator">
              <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
              Live · {lastRefresh.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={refreshStatus}
            className="flex items-center gap-1 px-2 py-1.5 text-[12px] font-medium rounded-sm transition-colors"
            style={{ border: "1px solid var(--tga-border-strong)", color: "var(--tga-text-secondary)" }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = "var(--tga-bg-hover)"}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = ""}
            data-testid="manual-refresh-btn"
            title="Jetzt aktualisieren"
          >
            <RefreshCw className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={() => navigate("/app/gps-audit-log")}
            className="flex items-center gap-1 px-2.5 py-1.5 text-[12px] font-medium rounded-sm transition-colors"
            style={{ border: "1px solid var(--tga-border-strong)", color: "var(--tga-text-primary)" }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = "var(--tga-bg-hover)"}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = ""}
            data-testid="gps-audit-log-btn"
          >
            <Navigation className="w-3.5 h-3.5" />
            GPS Audit-Log
          </button>
          <button
            onClick={() => navigate("/app/mitarbeiter/neu")}
            className="flex items-center gap-1 px-2.5 py-1.5 text-[12px] font-medium rounded-sm transition-colors"
            style={{ backgroundColor: "var(--tga-blue)", color: "#fff" }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = "var(--tga-blue-hover)"}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = "var(--tga-blue)"}
            data-testid="add-user-btn"
          >
            <Plus className="w-3.5 h-3.5" />
            Neuer Mitarbeiter
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="border overflow-x-auto" style={{ borderRadius: 2, backgroundColor: "var(--tga-bg-card)", borderColor: "var(--tga-border)" }}>
        <table className="w-full" style={{ borderCollapse: "collapse", minWidth: 900 }}>
          <thead>
            <tr style={{ backgroundColor: "var(--tga-bg-elevated)", borderBottom: "1px solid var(--tga-border-strong)" }}>
              <th className="text-center text-[11px] font-semibold uppercase tracking-wider px-1 py-1.5" style={{ color: "var(--tga-text-muted)", borderRight: "1px solid var(--tga-border)", width: 28 }} title="Ziehen zum Sortieren"></th>
              <th className="text-left text-[11px] font-semibold uppercase tracking-wider px-2 py-1.5" style={{ color: "var(--tga-text-muted)", borderRight: "1px solid var(--tga-border)" }}>Name</th>
              <th className="text-left text-[11px] font-semibold uppercase tracking-wider px-2 py-1.5" style={{ color: "var(--tga-text-muted)", borderRight: "1px solid var(--tga-border)" }}>E-Mail</th>
              <th className="text-left text-[11px] font-semibold uppercase tracking-wider px-2 py-1.5" style={{ color: "var(--tga-text-muted)", borderRight: "1px solid var(--tga-border)", width: 100 }}>Rolle</th>
              <th className="text-left text-[11px] font-semibold uppercase tracking-wider px-2 py-1.5" style={{ color: "var(--tga-text-muted)", borderRight: "1px solid var(--tga-border)", width: 80 }}>Status</th>
              <th className="text-center text-[11px] font-semibold uppercase tracking-wider px-2 py-1.5" style={{ color: "var(--tga-text-muted)", borderRight: "1px solid var(--tga-border)", width: 90 }}>Heute</th>
              <th className="text-center text-[11px] font-semibold uppercase tracking-wider px-2 py-1.5" style={{ color: "var(--tga-text-muted)", borderRight: "1px solid var(--tga-border)", width: 50 }}>GPS</th>
              <th className="text-left text-[11px] font-semibold uppercase tracking-wider px-2 py-1.5" style={{ color: "var(--tga-text-muted)", borderRight: "1px solid var(--tga-border)", width: 180 }}>Standort</th>
              <th className="text-left text-[11px] font-semibold uppercase tracking-wider px-2 py-1.5" style={{ color: "var(--tga-text-muted)", borderRight: "1px solid var(--tga-border)", width: 140 }}>Projekt</th>
              <th className="text-left text-[11px] font-semibold uppercase tracking-wider px-2 py-1.5" style={{ color: "var(--tga-text-muted)", width: 50 }}></th>
            </tr>
          </thead>
          <tbody>
            {users.map((u, i) => {
              const ts = todayStatus[u.id];
              const isDragOver = dragOverIndex === i;
              return (
                <tr
                  key={u.id}
                  className="cursor-pointer"
                  style={{
                    borderBottom: "1px solid var(--tga-border)",
                    borderTop: isDragOver ? "2px solid var(--tga-blue, #3b82f6)" : undefined,
                    backgroundColor: isDragOver ? "rgba(59,130,246,0.06)" : undefined,
                  }}
                  onClick={() => navigate(`/app/mitarbeiter/${u.id}`)}
                  onDragOver={(e) => handleDragOver(e, i)}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => handleDrop(e, i)}
                  data-testid={`user-item-${u.id}`}
                >
                  <td
                    className="px-1 text-center align-middle"
                    style={{ height: 36, borderRight: "1px solid var(--tga-border)", width: 28, cursor: "grab" }}
                    draggable
                    onDragStart={(e) => handleDragStart(e, i)}
                    onClick={(e) => e.stopPropagation()}
                    title="Ziehen zum Sortieren"
                    data-testid={`user-drag-${u.id}`}
                  >
                    <GripVertical className="w-3.5 h-3.5 mx-auto" style={{ color: "var(--tga-text-muted)" }} />
                  </td>
                  <td className="px-2 text-[13px] font-medium" style={{ height: 36, borderRight: "1px solid var(--tga-border)", color: "var(--tga-text-bright)" }}>{u.name}</td>
                  <td className="px-2 text-[13px]" style={{ height: 36, borderRight: "1px solid var(--tga-border)", color: "var(--tga-text-secondary)" }}>{u.email}</td>
                  <td className="px-2 text-[12px]" style={{ height: 36, borderRight: "1px solid var(--tga-border)", color: "var(--tga-text-secondary)" }}>{roleLabels[u.role] || u.role}</td>
                  <td className="px-2 text-[12px] font-medium" style={{ height: 36, borderRight: "1px solid var(--tga-border)", color: u.is_active ? "var(--tga-green)" : "var(--tga-text-muted)" }}>{u.is_active ? "Aktiv" : "Inaktiv"}</td>
                  {/* Heute - Clock status */}
                  <td className="px-2 text-center" style={{ height: 36, borderRight: "1px solid var(--tga-border)" }} data-testid={`user-today-${u.id}`}>
                    {ts ? (
                      ts.clocked_in ? (
                        <span className="inline-flex items-center gap-1 text-[11px] font-medium text-green-700 bg-green-50 px-1.5 py-0.5 rounded">
                          <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
                          {ts.clock_in_time || "Aktiv"}
                        </span>
                      ) : ts.clocked_out ? (
                        <span className="inline-flex items-center gap-1 text-[11px] font-medium text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded">
                          <Clock className="w-3 h-3" />
                          Fertig
                        </span>
                      ) : null
                    ) : (
                      <span className="text-[11px] text-gray-300">—</span>
                    )}
                  </td>
                  {/* GPS */}
                  <td className="px-2 text-center" style={{ height: 36, borderRight: "1px solid var(--tga-border)" }} data-testid={`user-gps-${u.id}`}>
                    {ts ? (
                      ts.has_gps ? (
                        <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-green-100" title="GPS aktiv">
                          <Navigation className="w-3 h-3 text-green-600" />
                        </span>
                      ) : (
                        <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-red-100" title="Ohne GPS">
                          <Navigation className="w-3 h-3 text-red-400" />
                        </span>
                      )
                    ) : (
                      <span className="text-[11px] text-gray-300">—</span>
                    )}
                  </td>
                  {/* Standort */}
                  <td className="px-2" style={{ height: 36, borderRight: "1px solid var(--tga-border)" }} data-testid={`user-location-${u.id}`}>
                    {ts && ts.location_address ? (
                      <div className="flex items-center gap-1">
                        <MapPin className="w-3 h-3 shrink-0 text-gray-400" />
                        <span className="text-[11px] text-gray-600 truncate" style={{ maxWidth: 160 }} title={ts.location_address}>
                          {ts.location_address}
                        </span>
                        {ts.location_status && (
                          <span className={`shrink-0 text-[9px] font-semibold px-1 py-0.5 rounded ${
                            ts.location_status === "vor_ort" ? "bg-green-100 text-green-700" : "bg-orange-100 text-orange-700"
                          }`}>
                            {ts.location_status === "vor_ort" ? "Vor Ort" : "Extern"}
                          </span>
                        )}
                      </div>
                    ) : (
                      <span className="text-[11px] text-gray-300">—</span>
                    )}
                  </td>
                  {/* Projekt */}
                  <td className="px-2" style={{ height: 36, borderRight: "1px solid var(--tga-border)" }} data-testid={`user-project-${u.id}`}>
                    {ts && ts.project_name ? (
                      <span className="text-[11px] text-gray-700 font-medium truncate block" style={{ maxWidth: 130 }} title={ts.project_name}>
                        {ts.project_name}
                      </span>
                    ) : (
                      <span className="text-[11px] text-gray-300">—</span>
                    )}
                  </td>
                  <td className="px-2 text-center" style={{ height: 36 }}>
                    {u.id !== user?.id && (
                      <button onClick={(e) => { e.stopPropagation(); handleDeleteUser(u.id); }} className="text-red-400 hover:text-red-600" data-testid={`delete-user-${u.id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
